import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getAwarenessEventById,
  getAwarenessSections,
  AwarenessEvent,
  AwarenessContentRow,
} from "../../../services/Writer/awarenessService";

const renderFormattedText = (text: string) => {
  if (!text) return '';
  const lines = text.split('\n').map(line => {
    const trimmed = line.trimStart();
    if (trimmed.startsWith('- ')) {
      return line.replace('- ', '• ');
    } else if (trimmed.startsWith('* ')) {
      return line.replace('* ', '• ');
    }
    return line;
  });
  const processedText = lines.join('\n');
  const parts = processedText.split(/(\*[^*]+\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('*') && part.endsWith('*')) {
      const content = part.slice(1, -1);
      return (
        <strong key={index} className="font-bold text-gray-900 dark:text-white">
          {content}
        </strong>
      );
    }
    return part;
  });
};

const AwarenessViewPage: React.FC = () => {
  const { event_id } = useParams<{ event_id: string }>();
  const navigate = useNavigate();

  const [event, setEvent] = useState<AwarenessEvent | null>(null);
  const [sections, setSections] = useState<AwarenessContentRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!event_id) return;

    const loadData = async () => {
      try {
        setLoading(true);

        const eventData = await getAwarenessEventById(event_id);
        const sectionData = await getAwarenessSections(event_id);

        setEvent(eventData);
        setSections(sectionData);
      } catch (error) {
        console.error("Failed to load awareness data", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [event_id]);

  if (loading) {
    return (
      <div className="p-10 text-center text-lg">
        Loading awareness preview...
      </div>
    );
  }

  if (!event) {
    return (
      <div className="p-10 text-center text-red-500">
        Awareness event not found
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">

      {/* Back Button */}
      <button
        onClick={() => navigate("/writer/awareness")}
        className="mb-6 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
      >
        ← Back
      </button>

      {/* Featured Image */}
      {event.featured_image && (
        <div className="mb-6">
          <img
            src={event.featured_image}
            alt={event.title}
            className="w-full rounded-lg shadow"
          />
        </div>
      )}

      {/* Title */}
      <h1 className="text-3xl font-bold mb-2 text-gray-900 dark:text-white">
        {event.title}
      </h1>

      {/* Theme */}
      {event.theme && (
        <p className="text-sm text-gray-500 mb-4">
          Theme: {event.theme}
        </p>
      )}

      {/* Short Description */}
      {event.short_description && (
        <p className="text-lg mb-8 text-gray-700 dark:text-gray-300 whitespace-pre-line">
          {renderFormattedText(event.short_description)}
        </p>
      )}

      {/* Sections */}
      <div className="space-y-8">

        {sections.map((section) => (
          <div key={section.id}>

            <h2 className="text-xl font-semibold mb-2 capitalize text-gray-900 dark:text-white">
              {section.section}
            </h2>

            <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
              {renderFormattedText(section.content)}
            </div>

          </div>
        ))}

      </div>
    </div>
  );
};

export default AwarenessViewPage;